import { JSX, useEffect, useState } from 'react';
import { Controller, UseFormReturn } from 'react-hook-form';

import { ErrorMessage } from '@hookform/error-message';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore framer-motion is in the parent dir
import { AnimatePresence, motion } from 'framer-motion';
import { CheckIcon, EyeIcon, PencilIcon, TrashIcon, XIcon } from 'lucide-react';

import { Label } from 'components/Label';
import { Select } from 'components/Select';
import { Flex, Grid } from 'components/layout';
import { Typography } from 'components/typography';
import { CostLineItem, NormalizedLoad } from 'types/Load';
import { Maybe } from 'types/UtilityTypes';
import { formatCurrency } from 'utils/formatCurrency';
import { cn } from 'utils/shadcn';

import { LoadBuildingTextInput } from '../../QuantumEdgeLoadForm';
import {
  quantumEdgeCostTypesList,
  quantumEdgeUnitBasisList,
} from '../constants';

interface QuantumEdgeCostItemCardProps {
  index: number;
  isReadOnly: boolean;
  formMethods: UseFormReturn<NormalizedLoad>;
  onRemove: (index: number) => void;
  fieldPath: 'rateData.customerLineItems' | 'rateData.carrierLineItems';
}

const ANIMATIONS = {
  card: {
    initial: { opacity: 0 },
    animate: { opacity: 1 },
    exit: { opacity: 0 },
  },
  toggle: {
    initial: { opacity: 0, height: 0 },
    animate: { opacity: 1, height: 'auto' },
    exit: { opacity: 0, height: 0 },
    transition: { duration: 0.15 },
  },
  actions: {
    initial: { opacity: 0, scale: 0.8 },
    animate: { opacity: 1, scale: 1 },
    exit: { opacity: 0, scale: 0.8 },
    transition: { duration: 0.1 },
  },
};

export function QuantumEdgeCostItemCard({
  index,
  isReadOnly,
  formMethods,
  onRemove,
  fieldPath,
}: QuantumEdgeCostItemCardProps): JSX.Element {
  const {
    control,
    watch,
    setValue,
    formState: { errors },
    trigger,
  } = formMethods;
  const [isEditing, setIsEditing] = useState(false);
  const [originalValues, setOriginalValues] =
    useState<Maybe<CostLineItem>>(null);

  const watchedCharge = watch(`${fieldPath}.${index}`) as CostLineItem;
  const watchedQuantity = watch(`${fieldPath}.${index}.quantity`);
  const watchedRatePerUnit = watch(`${fieldPath}.${index}.ratePerUnitUSD`);

  // Calculate total charge when quantity or rate changes
  useEffect(() => {
    const quantity = watchedQuantity || 0;
    const ratePerUnit = watchedRatePerUnit || 0;
    const calculatedTotal = quantity * ratePerUnit;

    if (calculatedTotal !== watchedCharge?.totalChargeUSD) {
      setValue(`${fieldPath}.${index}.totalChargeUSD`, calculatedTotal, {
        shouldDirty: true,
        shouldValidate: true,
      });
    }
  }, [
    watchedQuantity,
    watchedRatePerUnit,
    watchedCharge?.totalChargeUSD,
    setValue,
    index,
    fieldPath,
  ]);

  const isEmptyCharge = !watchedCharge?.label && !watchedCharge?.unitBasis;

  // Start in editing mode if this is an empty charge
  useEffect(() => {
    if (isEmptyCharge && !isEditing) {
      setIsEditing(true);
    }
  }, [isEmptyCharge, isEditing]);

  // Use effect to default quantity to 1 if type contains "flat"
  useEffect(() => {
    if (watchedCharge?.unitBasis?.toLowerCase().includes('flat')) {
      setValue(`${fieldPath}.${index}.quantity`, 1);
    }
  }, [watchedCharge?.unitBasis, setValue, index, fieldPath]);

  // Handlers for the edit, save, discard, and remove charge actions
  const handlers = {
    edit: () => {
      setOriginalValues({ ...watchedCharge });
      setIsEditing(true);
    },
    save: async () => {
      const isValid = await trigger(`${fieldPath}.${index}`);
      if (!isValid) {
        return;
      }
      setIsEditing(false);
      setOriginalValues(null);
    },
    discard: () => {
      if (originalValues) {
        setValue(`${fieldPath}.${index}`, originalValues);
      }
      setIsEditing(false);
      setOriginalValues(null);
    },
    remove: () => onRemove(index),
  };

  const ActionButton = ({
    onClick,
    className,
    icon: Icon,
    'aria-label': ariaLabel,
  }: {
    onClick: () => void;
    className: string;
    icon: any;
    'aria-label': string;
  }) => (
    <motion.button
      whileHover={{ scale: 1.05 }}
      whileTap={{ scale: 0.95 }}
      onClick={onClick}
      className={`p-1 rounded-full transition-colors hover:cursor-pointer ${className}`}
      type='button'
      aria-label={ariaLabel}
      title={ariaLabel}
    >
      <Icon className='h-4 w-4' />
    </motion.button>
  );

  // Render the actions for the charge input card
  const renderActions = () => (
    <AnimatePresence mode='wait'>
      {isEditing ? (
        <motion.div
          key='editing-actions'
          {...ANIMATIONS.actions}
          className='flex gap-1.5'
        >
          {!isEmptyCharge ? (
            <>
              <ActionButton
                onClick={handlers.save}
                className='hover:bg-info-200 text-info-600'
                icon={CheckIcon}
                aria-label='Save changes'
              />
              {!isReadOnly && (
                <ActionButton
                  onClick={handlers.discard}
                  className='hover:bg-error-200 text-error-600'
                  icon={XIcon}
                  aria-label='Discard changes'
                />
              )}
            </>
          ) : (
            !isReadOnly && (
              <ActionButton
                onClick={handlers.remove}
                className='hover:bg-error-300 text-error-600'
                icon={TrashIcon}
                aria-label='Remove charge'
              />
            )
          )}
        </motion.div>
      ) : (
        <motion.div
          key='view-actions'
          {...ANIMATIONS.actions}
          className='flex gap-1.5'
        >
          {/* If read only, show the view button */}
          {isReadOnly && (
            <ActionButton
              onClick={handlers.edit}
              className='hover:bg-info-300 text-info-600'
              icon={EyeIcon}
              aria-label='View charge'
            />
          )}
          {/* If not read only, show the edit and remove buttons */}
          {!isReadOnly && (
            <>
              <ActionButton
                onClick={handlers.edit}
                className='hover:bg-info-300 text-info-600'
                icon={PencilIcon}
                aria-label='Edit charge'
              />

              <ActionButton
                onClick={handlers.remove}
                className='hover:bg-error-400 text-error-600'
                icon={TrashIcon}
                aria-label='Remove charge'
              />
            </>
          )}
        </motion.div>
      )}
    </AnimatePresence>
  );

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      const target = e.target as HTMLElement;

      if (
        target.closest('.ant-select') ||
        target.closest('.ant-select-dropdown') ||
        target.classList.contains('ant-select-search__field') ||
        target.closest('.ant-select-selection-search')
      ) {
        return;
      }

      if (isEditing) {
        e.preventDefault();
        e.stopPropagation();
        handlers.save();
      } else {
        return;
      }
    }
  };

  const renderEditingForm = () => (
    <motion.div
      key='editing-form'
      {...ANIMATIONS.toggle}
      className='overflow-hidden'
    >
      <Flex justify='between' align='center' className='mb-2'>
        <Flex align='center' gap='md'>
          <span className='text-xs font-semibold text-neutral-600'>
            Charge {index + 1}
          </span>
        </Flex>
        <div onClick={(e) => e.stopPropagation()}>{renderActions()}</div>
      </Flex>

      <Grid cols='2' gap='sm' className='mx-0 w-full'>
        <div className='col-span-2'>
          <Label name={`${fieldPath}.${index}.label`} className='text-xs'>
            Type
          </Label>
          <Controller
            name={`${fieldPath}.${index}.label`}
            control={control}
            rules={{ required: 'Type is required' }}
            render={({ field }) => (
              <Select
                showSearch
                className='h-8'
                placeholder={'Choose'}
                optionFilterProp='children'
                disabled={isReadOnly}
                onChange={field.onChange}
                value={field.value}
                options={quantumEdgeCostTypesList?.map((type) => ({
                  value: type,
                  label: type,
                }))}
              />
            )}
          />
          <ErrorMessage
            errors={errors}
            name={`${fieldPath}.${index}.label`}
            render={({ message }: { message: string }) => (
              <Typography variant='body-xs' className='text-error-500'>
                {message}
              </Typography>
            )}
          />
        </div>

        <div className='col-span-1'>
          <Label name={`${fieldPath}.${index}.unitBasis`} className='text-xs'>
            Rate Qualifier
          </Label>
          <Controller
            name={`${fieldPath}.${index}.unitBasis`}
            control={control}
            rules={{ required: 'Rate qualifier is required' }}
            render={({ field }) => (
              <Select
                showSearch
                className='h-8'
                placeholder={'Choose'}
                optionFilterProp='children'
                disabled={isReadOnly}
                onChange={field.onChange}
                value={field.value}
                options={quantumEdgeUnitBasisList?.map((basis) => ({
                  value: basis,
                  label: basis,
                }))}
              />
            )}
          />
          <ErrorMessage
            errors={errors}
            name={`${fieldPath}.${index}.unitBasis`}
            render={({ message }: { message: string }) => (
              <Typography variant='body-xs' className='text-error-500'>
                {message}
              </Typography>
            )}
          />
        </div>

        <div className='col-span-1'>
          <LoadBuildingTextInput
            name={`${fieldPath}.${index}.quantity`}
            labelClassName='text-xs'
            label='Quantity'
            inputType='number'
            readOnly={isReadOnly}
            options={{
              valueAsNumber: true,
              min: 0,
              required: 'Quantity is required',
            }}
            required={true}
          />
        </div>

        <div className='col-span-1'>
          <LoadBuildingTextInput
            name={`${fieldPath}.${index}.ratePerUnitUSD`}
            labelClassName='text-xs'
            label='Rate per Unit (USD)'
            inputType='number'
            readOnly={isReadOnly}
            options={{
              valueAsNumber: true,
              min: 0,
            }}
            required={true}
          />
        </div>

        <div className='col-span-1'>
          <LoadBuildingTextInput
            name={`${fieldPath}.${index}.totalChargeUSD`}
            labelClassName='text-xs'
            label='Total Charge (USD)'
            inputType='number'
            options={{ valueAsNumber: true, min: 0 }}
            required={false}
            readOnly={true}
            inputClassName='no-spin-buttons'
          />
        </div>

        <div className='col-span-2'>
          <LoadBuildingTextInput
            name={`${fieldPath}.${index}.note`}
            labelClassName='text-xs'
            label='Note'
            placeholder='Additional details...'
            readOnly={isReadOnly}
            required={false}
          />
        </div>
      </Grid>
    </motion.div>
  );

  // Render the view mode/collapsed mode for the charge input card
  const renderViewMode = () => {
    if (!watchedCharge?.label) return null;

    return (
      <motion.div
        key='view-mode'
        {...ANIMATIONS.toggle}
        className='overflow-hidden cursor-pointer'
        onClick={handlers.edit}
        onKeyDown={(e: React.KeyboardEvent) =>
          e.key === 'Enter' && handlers.edit()
        }
        role='button'
        tabIndex={0}
        aria-label='Click to edit charge'
      >
        <Flex justify='between' align='center' className='min-w-0'>
          <Flex align='center' gap='md' className='flex-1 min-w-0 mr-2'>
            <Typography
              variant='caption'
              className='text-neutral-600 font-medium truncate'
              title={watchedCharge.label}
              aria-label={watchedCharge.label}
            >
              {watchedCharge.label}
            </Typography>
          </Flex>

          <Flex onClick={(e) => e.stopPropagation()} className='flex-shrink-0'>
            {renderActions()}
          </Flex>
        </Flex>

        <Flex align='center' gap='sm' className='text-sm text-neutral-900'>
          <Typography variant='body-xs' className='text-neutral-500'>
            Qty:
          </Typography>
          <Typography variant='body-xs' className='text-neutral-600'>
            {watchedCharge.quantity}
          </Typography>
          <Typography variant='body-xs' className='text-neutral-500'>
            •
          </Typography>
          <Typography
            variant='body-xs'
            className='font-semibold text-brand-main'
          >
            {formatCurrency(watchedCharge.totalChargeUSD, 'USD')}
          </Typography>
        </Flex>
        {watchedCharge.note && (
          <Typography variant='body-xs' className='text-neutral-500 mt-1'>
            {watchedCharge.note}
          </Typography>
        )}
      </motion.div>
    );
  };

  return (
    <motion.div
      layout
      transition={{ type: 'spring', duration: 0.2, bounce: 0 }}
      {...ANIMATIONS.card}
      className={cn(
        'w-full overflow-hidden rounded-[4px] border bg-linear-to-br shadow-sm relative transition-all duration-200 p-2.5',
        isEmptyCharge
          ? 'border-neutral-400 bg-linear-to-br from-neutral-50 to-neutral-100/50'
          : 'border-neutral-500 bg-linear-to-br from-neutral-50 to-info-50/50'
      )}
      onKeyDown={handleKeyDown}
    >
      <AnimatePresence mode='wait'>
        {isEditing ? renderEditingForm() : renderViewMode()}
      </AnimatePresence>
    </motion.div>
  );
}
